<script setup lang="ts">
import ProgressFrame from './layout/ProgressFrame.vue';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import { useCommissioningDeviceStore } from '../stores/commissioningDeviceStore';
import { useRouter } from 'vue-router';
import { useDeviceSpecificationStore } from '../stores/deviceSpecificationStore';
import { message } from 'ant-design-vue';
import { COMMISSION_CONFIG_STATUS, COMMISSION_STATUS } from '../utils/constants';
import { usePollingStore } from '../stores/pollingStore';
import { PollKey } from '../models/Poll';

const pollingStore = usePollingStore();
const deviceSpecificationStore = useDeviceSpecificationStore();
const commissioningDeviceStore = useCommissioningDeviceStore();
const serialNumber = commissioningDeviceStore.serialNumber;

const router = useRouter();
const startCommissioning = async () => {
  // Just call this in case the polling has not been started yet.
  pollingStore.start(PollKey.WIFI_STATUS);

  // If config hasn't been successfully uploaded, ensure we upload it first 
  // before starting the commissioning process
  commissioningDeviceStore.isUploadConfig ||=
    commissioningDeviceStore.configUploadLog.status !== COMMISSION_CONFIG_STATUS.SUCCESS;

  if (commissioningDeviceStore.isUploadConfig) {
    try {
      commissioningDeviceStore.resetCommissionProcess();
      commissioningDeviceStore.setUploadConfigState(COMMISSION_CONFIG_STATUS.PENDING);
      await deviceSpecificationStore.uploadAllCommissionConfig(serialNumber);
      router.push({ name: 'CommissionInProgress' });
    } catch (error: unknown) {
      commissioningDeviceStore.setUploadConfigState(COMMISSION_CONFIG_STATUS.FAILED);
      console.log('Failed to submit allocations config', error);
      message.error('Failed to submit allocations config. Please try again.');
    }
  } else {
    try {
      commissioningDeviceStore.resetCommissionProcess([
        'commissioningLog',
        'commissionResultStatus',
      ]);
      commissioningDeviceStore.setCommissioningLogState(COMMISSION_STATUS.READY);
      await commissioningDeviceStore.startCommission(serialNumber);
      router.push({ name: 'CommissionInProgress' });
    } catch (error: unknown) {
      commissioningDeviceStore.setCommissioningLogState(COMMISSION_STATUS.FAILED);
      console.log('Failed to start commission process', error);
      message.error('Failed to start commission process. Please try again.');
    }
  }
};
</script>

<template>
  <ProgressFrame pageClass="section-page">
    <template #title>
      <div class="title-container">Commissioning Tests</div>
    </template>
    <template #subTitle>
      <p class="serial-number">{{ serialNumber }}</p>
    </template>
    <div class="content">
      <h2>Great, almost there.</h2>
      <p class="instruction-text" data-tn="instruction-text">
        We're now going to run some active tests down the line to check everything is connected and
        configured correctly.
      </p>
      <div class="action-container">
        <ButtonPrimary block @click="startCommissioning" data-tn="next-btn">
          Start Commissioning
        </ButtonPrimary>
      </div>
    </div>
  </ProgressFrame>
</template>

<style lang="less" scoped>
.title-container {
  margin-top: 2rem;
}
.serial-number {
  font-weight: @font-weight-medium;
  color: @color-primary;
}
.content {
  text-align: center;
  .instruction-text {
    font-weight: @font-weight-medium;
  }
}
.action-container {
  width: 100%;
  margin-top: @margin-top-end-of-page-section;
}
</style>
